/**
 * Вложения к сообщению: проверка, вид, сборка формы для Chatwoot.
 *
 * Chatwoot принимает файлы одним multipart-запросом `POST conversations/:id/messages`:
 * `attachments[]` (по одному полю на файл), `content` (подпись, по желанию), `private`, `echo_id`,
 * `is_voice_message=true` — для голосового (Messages::MessageBuilder#tag_voice_message
 * ставит вложению пометку is_voice_message, по ней Telegram/WhatsApp шлют его как голосовое).
 * В нашей копии есть именно is_voice_message; `is_recorded_audio` в ней нет.
 */
import type { CwAttachment } from '@/api/inboxTypes';

/**
 * Предел размера одного файла: MAXIMUM_FILE_UPLOAD_SIZE Chatwoot (по умолчанию 40 МБ,
 * config/installation_config.yml; Attachment#validate_file_size). Настоящее значение API не отдаёт
 * (панель берёт его из window.chatwootConfig), поэтому проверяем по умолчанию, а всё, что сервер
 * всё-таки отклонит, показываем его же текстом.
 */
export const MAX_FILE_BYTES = 40 * 1024 * 1024;
/** Сколько файлов за раз: на телефоне больше десяти снимков в одно сообщение никто не шлёт. */
export const MAX_FILES = 10;

export type FileKind = 'image' | 'video' | 'audio' | 'file';

export function fileKind(f: { type: string; name?: string }): FileKind {
  const t = f.type.toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  return 'file';
}

export interface Rejected { name: string; reason: string }

/** Мегабайты по-русски: «41,3 МБ». */
const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;

/** Отбор файлов: влезающие в предел и их число — принимаем, остальные возвращаем с причиной. */
export function pickFiles(
  incoming: File[],
  already: number,
): { accepted: File[]; rejected: Rejected[] } {
  const accepted: File[] = [];
  const rejected: Rejected[] = [];
  for (const f of incoming) {
    if (f.size <= 0) {
      rejected.push({ name: f.name, reason: 'файл пустой' });
    } else if (f.size > MAX_FILE_BYTES) {
      rejected.push({ name: f.name, reason: `больше ${mb(MAX_FILE_BYTES)} (у файла ${mb(f.size)})` });
    } else if (already + accepted.length >= MAX_FILES) {
      rejected.push({ name: f.name, reason: `за раз можно не больше ${MAX_FILES} файлов` });
    } else {
      accepted.push(f);
    }
  }
  return { accepted, rejected };
}

export interface FormInput {
  content: string;
  isPrivate: boolean;
  echoId: string;
  files: File[];
  /** Голосовое сообщение (запись с микрофона). */
  isVoice?: boolean;
}

/** Сборка multipart-формы, как её собирает панель Chatwoot (api/inbox/message.js → buildCreatePayload). */
export function buildMessageForm(input: FormInput): FormData {
  const form = new FormData();
  const text = input.content.trim();
  if (text) form.append('content', text);
  input.files.forEach((f) => form.append('attachments[]', f, f.name));
  form.append('private', input.isPrivate ? 'true' : 'false');
  form.append('echo_id', input.echoId);
  form.append('message_type', 'outgoing');
  if (input.isVoice) form.append('is_voice_message', 'true');
  return form;
}

let localAttId = 0;

/** Временные «вложения» для пузыря до ответа сервера: показывают то, что отправляется (по ссылке из памяти). */
export function localAttachments(files: File[], urlOf: (f: File) => string): CwAttachment[] {
  return files.map((f) => {
    localAttId -= 1;
    return {
      id: localAttId,
      file_type: fileKind(f),
      data_url: urlOf(f),
      thumb_url: fileKind(f) === 'image' ? urlOf(f) : undefined,
      file_size: f.size,
      fallback_title: f.name,
    };
  });
}
